import { sentriLockApi } from "api/sentrilock";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { ConnectIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

type SentriLockTab = "Credentials";

const SENTRILOCK_TABS: Tab<SentriLockTab>[] = [
  { value: "Credentials", label: "Credentials" }
];

export const SentriLockView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<SentriLockTab>("Credentials");

  const { mutate: disconnectSentriLock, isPending: isDisconnecting } =
    useMutation({
      mutationKey: ["SENTRILOCK", "DELETE"],
      mutationFn: () => sentriLockApi.disconnect(),
      onSuccess: () => {
        toast.success("SentriLock disconnected successfully");
        refreshPartnerData();
        navigate("/settings/integration");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: updateSentriLock, isPending } = useMutation({
    mutationKey: ["SENTRILOCK", "UPDATE"],
    mutationFn: () => sentriLockApi.connect({ username, password }),
    onSuccess: () => {
      toast.success("SentriLock credentials updated successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Layout
      headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-6 pb-[22px]">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "SentriLock",
                  href: "/settings/integration/sentrilock"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <IntegrationLogoIcon id={IntegrationId.SENTRILOCK} size="SM" />
              <p className="text-xl font-bold text-content-1000">SentriLock</p>
            </div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={ConnectIcon}
              onClick={() => disconnectSentriLock()}
              loading={isDisconnecting}
            >
              Disconnect
            </Button>
          </div>
          <div className="flex items-center p-6 border-b border-alpha-40">
            <div className="space-y-2">
              <p className="text-xs text-content-400">Integration type</p>
              <p className="text-sm font-medium text-content-1000">
                SentriLock
              </p>
            </div>
          </div>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => navigate("/settings/integration")}
            className="w-[125px]"
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => updateSentriLock()}
            className="w-[125px]"
            disabled={!username || !password}
            loading={isPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <Tabs
        tabs={SENTRILOCK_TABS}
        active={activeTab}
        onChange={setActiveTab}
        tabStyle="line"
        size="md"
        fullWidthTabs={false}
      />
      <Card className="w-full p-6 mt-4">
        <p className="text-base font-bold text-content-1000">Credentials</p>
        <div className="mt-6 border-t border-alpha-40 pt-6 space-y-4">
          <BaseInput
            label="Username"
            required
            id="sentrilock-username"
            placeholder="Username"
            value={username}
            onChange={setUsername}
          />
          <BaseInput
            label="Password"
            required
            id="sentrilock-password"
            type="password"
            placeholder="**********************"
            value={password}
            onChange={setPassword}
          />
        </div>
      </Card>
    </Layout>
  );
};
